import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { Autocomplete, TextField } from '@mui/material';

interface PromptTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  blockId: string;
  blockName: string;
  initialPrompt: string;
  defaultModel?: string;
  onSavePrompt: (prompt: string, model?: string) => void;
}

export const PromptTestModal: React.FC<PromptTestModalProps> = ({
  isOpen,
  onClose,
  blockId,
  blockName,
  initialPrompt,
  defaultModel,
  onSavePrompt
}) => {
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [actualModel, setActualModel] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [model, setModel] = useState(defaultModel || localStorage.getItem('default_model') || '');
  const outputRef = useRef<HTMLDivElement>(null);

  // Add model selection dropdown
  const [modelOptions, setModelOptions] = useState<{id: string, name: string, provider?: string, displayName?: string, deprecated?: boolean}[]>([]);

  // Load enabled models
  useEffect(() => {
    const loadEnabledModels = async () => {
      try {
        const response = await api.getAllModels();
        const models = response.models || [];
        const modelOptions = models.map((model: any) => ({
          id: model.id,
          name: model.name,
          provider: model.provider || 'unknown',
          displayName: `${(model.provider || 'unknown').toUpperCase()} / ${model.name}`,
          deprecated: model.deprecated || false
        }));
        setModelOptions(modelOptions);
      } catch (error) {
        console.error('Failed to load enabled models:', error);
        // Keep fallback models
      }
    };
    
    loadEnabledModels();
  }, []);

  useEffect(() => {
    setPrompt(initialPrompt || '');
    setInput('');
    setOutput('');
    setActualModel(null);
    setError(null);
    setIsSaved(false);
    setModel(defaultModel || localStorage.getItem('default_model') || '');
  }, [isOpen, initialPrompt, defaultModel]);

  const handleSavePrompt = () => {
    onSavePrompt(prompt, model);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleTestPrompt = async () => {
    try {
      setIsLoading(true);
      setOutput('');
      setActualModel(null);
      setError(null);
      
      // Use non-streaming API which might handle whitespace better
      const result = await api.testPrompt(prompt, input, model);
      setOutput(result.response);
      setActualModel(result.actualModel || null);
      
      // Auto-scroll to bottom
      if (outputRef.current) {
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
      }
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2 flex items-center justify-between">
                  <span>Test Block: {blockName}</span>
                  {isSaved && (
                    <span className="text-sm text-green-600 animate-pulse ml-4">
                      ✓ Prompt saved!
                    </span>
                  )}
                </h3>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 font-medium whitespace-nowrap">
                      Model:
                    </label>
                    <div className="flex-1" style={{ minWidth: '300px' }}>
                      <Autocomplete
                        fullWidth
                        options={modelOptions}
                        value={modelOptions.find((option) => option.id === model) || null}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            setModel(newValue.id);
                          }
                        }}
                        getOptionLabel={(option) => {
                          const label = option.displayName || option.name;
                          return option.deprecated ? `${label} (DEPRECATED)` : label;
                        }}
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          const searchLower = inputValue.toLowerCase();
                          return options.filter((option) =>
                            option.name.toLowerCase().includes(searchLower) ||
                            (option.displayName && option.displayName.toLowerCase().includes(searchLower)) ||
                            (option.provider && option.provider.toLowerCase().includes(searchLower))
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search models..."
                            variant="outlined"
                            size="small"
                          />
                        )}
                        ListboxProps={{
                          style: { maxHeight: 400 }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {(() => {
                  const selectedModel = modelOptions.find(option => option.id === model);
                  return selectedModel?.deprecated ? (
                    <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className="text-sm text-yellow-800 font-medium">
                          This model is deprecated and may not work properly. Consider switching to a newer model.
                        </span>
                      </div>
                    </div>
                  ) : null;
                })()}

                <div className="mb-4">
                  <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Template
                  </label>
                  <textarea
                    id="prompt"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm font-mono h-40"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Enter your prompt template here..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This is the system prompt that will be used to process your input. Write clear instructions for the AI.
                  </p>
                </div>

                <div className="mb-4">
                  <label htmlFor="input" className="block text-sm font-medium text-gray-700 mb-1">
                    Test Input
                  </label>
                  <textarea
                    id="input"
                    className="w-full p-3 border border-gray-300 rounded-md text-sm h-24"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter test input here..."
                  />
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <label htmlFor="output" className="block text-sm font-medium text-gray-700">
                      Output
                    </label>
                    {isLoading && (
                      <span className="text-sm text-blue-600 animate-pulse">
                        Generating response...
                      </span>
                    )}
                  </div>
                  <div 
                    ref={outputRef}
                    className="w-full p-3 border border-gray-300 rounded-md text-sm bg-gray-50 h-48 overflow-y-auto whitespace-pre-wrap"
                  >
                    {output || (
                      <span className="text-gray-400 italic">
                        Response will appear here...
                      </span>
                    )}
                  </div>
                  {actualModel && (
                    <div className="mt-2 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-block">
                      <span className="font-semibold">Actual Model Used:</span> {actualModel}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleSavePrompt}
            >
              Save Prompt
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleTestPrompt}
              disabled={isLoading}
            >
              Test
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 